import { Controller } from '@hotwired/stimulus';

/**
 * Controlador Stimulus para enlazar dos o más selects en cascada (sin jQuery).
 *
 * El select hijo filtra sus opciones basándose en el valor del select padre.
 * Las opciones del select hijo deben tener un atributo data-padre-id con el valor
 * del padre al que pertenecen (puede ser múltiple, separado por espacios).
 *
 * CARACTERÍSTICAS:
 * - No requiere jQuery
 * - Genérico y reutilizable
 * - Mantiene siempre la opción vacía
 * - Resetea el valor si no está disponible
 * - Opcional: deshabilita si no hay opciones disponibles
 *
 * EJEMPLO DE USO:
 *
 * 1. Select padre (sin necesidad de controlador):
 *    <select id="mi-centro-banco">
 *      <option value="">Seleccionar</option>
 *      <option value="1">Centro Banco 1</option>
 *      <option value="2">Centro Banco 2</option>
 *    </select>
 *
 * 2. Select hijo (vinculado al padre):
 *    <select data-controller="inputs--selector-encadenado"
 *            data-padre-selector="#mi-centro-banco"
 *            data-deshabilitar-si-vacio>
 *      <option value="">Seleccionar</option>
 *      <option value="cc1" data-padre-id="1">Cuenta Contable 1</option>
 *      <option value="cc2" data-padre-id="1" data-seleccionado="true">
 *        Cuenta Contable 2 (por defecto)
 *      </option>
 *      <option value="cc3" data-padre-id="2">Cuenta Contable 3</option>
 *    </select>
 *
 * PARÁMETROS DATA:
 * - data-padre-selector: Selector CSS del select padre (#id)
 * - data-deshabilitar-si-vacio: Si está presente, deshabilita si no hay opciones
 * - data-mostrar-todo-si-vacio: Si está presente, muestra todas las opciones cuando el padre no tiene valor seleccionado
 * - data-seleccionado="true": En una opción del hijo, la marca como seleccionada por defecto
 */
export default class SelectorEncadenadoController extends Controller {
  connect() {
    this.respaldoOpcionesHtml = this.element.innerHTML;
    this.selectorPadre = this.element.dataset.padreSelector;
    this.debeDeshabilitarse = Object.prototype.hasOwnProperty.call(
      this.element.dataset,
      'deshabilitarSiVacio',
    );
    this.mostrarTodoSiVacio = Object.prototype.hasOwnProperty.call(
      this.element.dataset,
      'mostrarTodoSiVacio',
    );

    if (!this.selectorPadre) {
      throw new Error('SelectorEncadenadoController: falta atributo data-padre-selector');
    }

    this.elementoPadre = document.querySelector(this.selectorPadre);
    if (!this.elementoPadre) {
      throw new Error(`SelectorEncadenadoController: no se encontró elemento padre con selector "${this.selectorPadre}"`);
    }

    // Escuchar cambios en el select padre
    this.manejadorCambio = () => this.actualizarOpciones();
    this.elementoPadre.addEventListener('change', this.manejadorCambio);

    // Actualizar al conectar
    this.actualizarOpciones();
  }

  disconnect() {
    if (this.elementoPadre && this.manejadorCambio) {
      this.elementoPadre.removeEventListener('change', this.manejadorCambio);
    }
  }

  actualizarOpciones() {
    const valorPadre = this.elementoPadre.value;

    // Guardar el valor actualmente seleccionado
    const valorActual = this.element.value;

    // Restaurar todas las opciones originales
    this.element.innerHTML = this.respaldoOpcionesHtml;

    // Si el padre está vacío y se debe mostrar todo, no filtrar
    if (this.mostrarTodoSiVacio && valorPadre === '') {
      if (valorActual) {
        this.element.value = valorActual;
      }
      this.actualizarEstadoDeshabilitado();
      return;
    }

    let valorEstabaDisponible = false;

    // Filtrar opciones
    Array.from(this.element.options).forEach((opcion) => {
      // Siempre mantener la opción vacía (valor = "")
      if (opcion.value === '') {
        return;
      }

      // Obtener los valores a los que esta opción está encadenada
      const datosEncadenados = opcion.dataset.padreId || '';
      const valoresPermitidos = datosEncadenados.split(' ').filter(v => v.length > 0);

      // Si la opción no está encadenada a ningún valor o no coincide con el padre, ocultarla
      if (!valoresPermitidos.includes(valorPadre)) {
        opcion.remove();
      } else if (opcion.value === valorActual) {
        valorEstabaDisponible = true;
      }
    });

    // Si el valor actual no está disponible, seleccionar el marcado por defecto o el vacío
    if (!valorEstabaDisponible) {
      const opcionPorDefecto = Array.from(this.element.options).find(
        (opcion) => opcion.dataset.seleccionado === 'true',
      );
      this.element.value = opcionPorDefecto ? opcionPorDefecto.value : '';
    }

    // Habilitar/deshabilitar según opciones disponibles
    this.actualizarEstadoDeshabilitado();
  }

  actualizarEstadoDeshabilitado() {
    // Contar opciones que NO sean la opción vacía
    const cantidadOpciones = Array.from(this.element.options).filter(
      (opt) => opt.value !== '',
    ).length;

    if (cantidadOpciones === 0 && this.debeDeshabilitarse) {
      this.element.disabled = true;
    } else {
      this.element.disabled = false;
    }
  }
}
